// src/useAppVersionCheck.js
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { APP_BUILD_ID } from "../appVersion";
import { hardReload } from "../utils/hardReload";

export const useAppVersionCheck = () => {
  const isLoggedIn = useSelector((state) => Boolean(state?.authReducer?.isLogin));
  const { pathname } = useLocation();
  const isLoginPage =
    pathname === "/user/login" || pathname.startsWith("/user/login/");

  useEffect(() => {
    if (!APP_BUILD_ID || isLoggedIn || !isLoginPage) {
      return undefined;
    }

    const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const versionUrl = `${publicUrl}/version.json`;
    let checking = false;
    let active = true;

    const checkForUpdate = async (force = false) => {
      if (
        !active ||
        checking ||
        (!force && document.visibilityState === "hidden")
      ) {
        return;
      }

      checking = true;
      try {
        const response = await fetch(versionUrl, { cache: "no-store" });
        if (!active) {
          return;
        }

        if (!response.ok) {
          throw new Error(`Version check failed with status ${response.status}`);
        }

        const { buildId } = await response.json();
        if (!active) {
          return;
        }

        if (!buildId) {
          throw new Error("Version check response did not contain a buildId");
        }

        if (buildId !== APP_BUILD_ID) {
          hardReload();
        }
      } finally {
        checking = false;
      }
    };

    const handleUpdateCheck = (force = false) => {
      void checkForUpdate(force).catch((error) => {
        console.error("Unable to check for application updates.", error);
      });
    };

    const handleVisibilityChange = () => handleUpdateCheck();
    const intervalId = globalThis.setInterval(() => handleUpdateCheck(), 60_000);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    handleUpdateCheck(true);

    return () => {
      active = false;
      globalThis.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoginPage, isLoggedIn]);
};
